import React from "react";
import styled from "styled-components";
import Window from "../Window";
import PixelIcon from "../PixelIcon";
import useSetting from "../../hooks/useSetting";
import { CRT_KEY } from "../CrtOverlay/CrtOverlay";

// Re-run the full boot/login scene on every visit (Stage reads this at
// load; lives here rather than in Stage to avoid a circular import).
export const ALWAYS_BOOT_KEY = "saat.alwaysBoot";

// What a first-time visitor gets; mirrors the fallbacks each consumer
// passes (sound.js volume, CrtOverlay strength, Stage's boot check).
const DEFAULT_VOLUME = 0.5;
const DEFAULT_CRT = 1;
const DEFAULT_ALWAYS_BOOT = false;
import {
  getVolume,
  setVolume,
  click as clickSound,
  volumeDing,
} from "../../sound/sound";

// Start → Settings. Piece-wide knobs: master volume (hum, clicks,
// cards), the CRT glass strength, and whether the boot scene replays
// every visit. Sliders are the 98.css range inputs; changes apply
// instantly and persist (the boot pref takes effect on the next load,
// since it gates the opening).

function SettingsWindow({ onClose, inactive = false }) {
  // iOS Safari sometimes paints this window's first layout wrong — extra
  // space below the Startup fieldset — and corrects itself on the next
  // interaction-forced reflow. No React re-render happens on
  // pointerdown, so this is the browser's own stale layout; nudge a
  // fresh layout + composite pass ourselves right after first paint.
  const rootRef = React.useRef(null);
  React.useEffect(() => {
    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = rootRef.current;
        if (!el) return;
        el.style.transform = "translateZ(0)";
        void el.offsetHeight; // force the reflow now, not on the tap
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
    };
  }, []);
  // Volume lives in sound.js; the setting event keeps this slider in
  // sync with the tray speaker, and vice versa.
  const [volSetting] = useSetting("saat.volume", getVolume());
  const vol = Math.round((volSetting ?? 0.5) * 100);
  const [crt, setCrt] = useSetting(CRT_KEY, 1);
  const [alwaysBoot, setAlwaysBoot] = useSetting(ALWAYS_BOOT_KEY, false);

  // Puts the three knobs in this window back where a fresh visitor
  // finds them. Deliberately NOT the deck or wallpaper — those live in
  // their own Win95 dialogs (Game → Deck…, Display Properties), and a
  // Control-Panel-style reset wouldn't touch them either.
  const restoreDefaults = () => {
    clickSound();
    setVolume(DEFAULT_VOLUME);
    setCrt(DEFAULT_CRT);
    setAlwaysBoot(DEFAULT_ALWAYS_BOOT);
  };
  const atDefaults =
    vol === Math.round(DEFAULT_VOLUME * 100) &&
    crt === DEFAULT_CRT &&
    alwaysBoot === DEFAULT_ALWAYS_BOOT;

  return (
    <StyledWindow
      title="Settings"
      icon={<PixelIcon name="settings" size={16} />}
      inactive={inactive}
      controls={["close"]}
      onControlClick={(c) => c === "close" && onClose && onClose()}
    >
      <Body ref={rootRef}>
        <fieldset>
          <legend>Volume</legend>
          <SliderRow>
            <PixelIcon name="speaker" size={16} />
            <input
              type="range"
              aria-label="Volume"
              min="0"
              max="100"
              value={vol}
              onChange={(e) => {
                setVolume(parseInt(e.target.value, 10) / 100);
              }}
              // Same ding as the tray slider: hear the level on release.
              onPointerUp={volumeDing}
              onKeyUp={(e) => {
                if (e.key.startsWith("Arrow")) volumeDing();
              }}
            />
            <PixelIcon name="speaker-loud" size={16} />
          </SliderRow>
        </fieldset>

        <fieldset>
          <legend>Monitor</legend>
          <div className="field-row">
            <input
              id="crt-enabled"
              type="checkbox"
              checked={crt > 0}
              onChange={(e) => {
                clickSound();
                setCrt(e.target.checked ? 1 : 0);
              }}
            />
            <label htmlFor="crt-enabled">CRT effect</label>
          </div>
          <SliderRow data-disabled={crt > 0 ? undefined : "true"}>
            <SliderLabel>subtle</SliderLabel>
            <input
              type="range"
              aria-label="CRT effect strength"
              min="20"
              max="140"
              value={Math.round((crt > 0 ? crt : 1) * 100)}
              disabled={!(crt > 0)}
              onChange={(e) => setCrt(parseInt(e.target.value, 10) / 100)}
            />
            <SliderLabel>strong</SliderLabel>
          </SliderRow>
        </fieldset>

        <fieldset>
          <legend>Startup</legend>
          <div className="field-row">
            <input
              id="always-boot"
              type="checkbox"
              checked={alwaysBoot === true}
              onChange={(e) => {
                clickSound();
                setAlwaysBoot(e.target.checked);
              }}
            />
            <label htmlFor="always-boot">
              Play the opening scene on every visit
            </label>
          </div>
        </fieldset>

        <ButtonRow>
          <button
            type="button"
            onClick={restoreDefaults}
            disabled={atDefaults}
          >
            Restore Defaults
          </button>
          <button type="button" onClick={onClose}>
            OK
          </button>
        </ButtonRow>
      </Body>
    </StyledWindow>
  );
}

const StyledWindow = styled(Window)`
  width: 300px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 6px 6px;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  & input[type="range"] {
    flex: 1 1 auto;
  }

  &[data-disabled="true"] {
    opacity: 0.5;
  }
`;

const SliderLabel = styled.span`
  color: #555;
  -webkit-user-select: none;
  user-select: none;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 6px;
`;

export default SettingsWindow;
